import { useFormContext, Controller, Path } from "react-hook-form";
import { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import Combobox from "@/components/ui/common/combobox";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { useGetAllMemberQuery } from "@/services/memberApi";
import { useGetCoachesQuery} from "@/services/coachApi";
import { format, differenceInYears, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { useGetQuestionsQuery, useGetConsultationAnswersQuery, useSubmitConsultationAnswersMutation, useCreateConsultationMutation, useUpdateConsultationMutation } from "@/services/consultationApi";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import DynamicQuestionRenderer from "./dynamic-question-renderer";
import { Consultation, FormQuestionResponse, ConsultationAnswer, ConsultationAnswerResponse } from "@/types/consultation";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "@radix-ui/react-icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FormControl } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { displayDate } from "@/lib/helper";
import { toast } from "@/components/ui/use-toast";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
  setAction?: (action: "add" | "edit") => void;
  refetch?: () => void;
  openSections?: string[];
  setOpenSections?: (sections: string[]) => void;
}

const Background = ({ action, open, setAction, refetch, openSections, setOpenSections }: FormStepProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    trigger,
    register,
  } = useFormContext<Consultation>();

  const watcher = watch();

  // Fetch members
  const { data: memberData } = useGetAllMemberQuery({
    query: "offset=0&sort_key=id&sort_order=desc&facility_type=consultation",
    org_id: orgId,
  }, { skip: !open });

  // Fetch coaches
  const { data: coachData } = useGetCoachesQuery({
    query: "offset=0&sort_key=id&sort_order=desc",
    org_id: orgId,
  }, { skip: !open });

  // Create member options
  const memberOptions = useMemo(() => {
    if (!Array.isArray(memberData?.data)) {
      return [];
    }
    return memberData?.data.map((member) => {
      const firstName = (member?.first_name || "").trim();
      const lastName = (member?.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");

      const labelPrefix = member.own_member_id
        ? `${member.own_member_id} • `
        : "";
      const label = `${labelPrefix}${fullName}`;

      return {
        label: label,
        value: member.id?.toString() || "",
      };
    });
  }, [memberData]);

  // Create coach options
  const coachOptions = useMemo(() => {
    if (!Array.isArray(coachData?.data)) {
      return [];
    }
    return coachData?.data.map((coach) => {
      const firstName = (coach?.first_name || "").trim();
      const lastName = (coach?.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");

      const labelPrefix = coach.own_coach_id ? `${coach.own_coach_id} • ` : "";
      const label = `${labelPrefix}${fullName}`;

      return {
        label: label,
        value: coach.id?.toString() || "",
      };
    });
  }, [coachData]);

  const selectedMember = useMemo(() => {
    if (!watcher.member_id || !memberData?.data) return null;
    return memberData.data.find((m) => m.id === Number(watcher.member_id));
  }, [watcher.member_id, memberData]);

  // Fetch questions
  const { data: questionsData } = useGetQuestionsQuery({
    org_id: orgId,
    section_key: ["medical", "nutrition", "exercise"],
  }, { skip: !open });

  const groupedQuestions = useMemo<Record<string, FormQuestionResponse[]>>(() => {
    return questionsData?.data || {};
  }, [questionsData]);

  const consultationId = watcher.id;
  
  const calculatedAge = useMemo(() => {
    if (!watcher.member_dob) return 0;
    try {
      return differenceInYears(new Date(), parseISO(watcher.member_dob));
    } catch (e) {
      return 0;
    }
  }, [watcher.member_dob]);

  const { data: answersData } = useGetConsultationAnswersQuery(
    { consultation_id: Number(consultationId), org_id: orgId, section_key: ["medical", "nutrition", "exercise"] },
    { skip: action !== "edit" || !consultationId || !open }
  );
  console.log({answersData})

  useEffect(() => {
    if (action === "edit" && answersData?.data) {
      Object.values(answersData.data).forEach((sectionAnswers: ConsultationAnswerResponse[]) => {
        if (Array.isArray(sectionAnswers)) {
          sectionAnswers.forEach((answer: ConsultationAnswerResponse) => {
            if (answer.question_id && answer.answer !== null) {
              setValue(`answers.${answer.question_id}`, answer.answer);
            }
          });
        }
      });
    }
  }, [answersData, action, setValue]);

  useEffect(() => {
    if (selectedMember) {
      const firstName = (selectedMember?.first_name || "").trim();
      const lastName = (selectedMember?.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      
      setValue("member_name", fullName);
      const dobValue = selectedMember.dob instanceof Date 
        ? selectedMember.dob.toISOString() 
        : selectedMember.dob;
      setValue("member_dob", dobValue || "");
      setValue("member_gender", selectedMember.gender || "");
      setValue("member_email", selectedMember.email || "");
    } else {
      setValue("member_name", "");
      setValue("member_dob", "");
      setValue("member_gender", "");
      setValue("member_email", "");
      setValue("membership_id", undefined);
    }
  }, [selectedMember, setValue]);

  const [submitConsultationAnswers] = useSubmitConsultationAnswersMutation();
  const [createConsultation, { isLoading: isCreating }] = useCreateConsultationMutation();
  const [updateConsultation, { isLoading: isUpdating }] = useUpdateConsultationMutation();
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [memberResetKey, setMemberResetKey] = useState(0);

  const handleSaveDetails = async () => {
    const formData = watcher;

    if (!formData.member_id || !formData.coach_id || !formData.membership_id || !formData.consultation_date) {
      toast({
        variant: "destructive",
        description: "Missing required consultation details. Please ensure Member, Coach, Membership, and Date are all selected.",
      });
      return;
    }
    
    try {
      if (action === "add") {
        const createPayload = {
          member_id: Number(formData.member_id),
          coach_id: Number(formData.coach_id),
          membership_id: Number(formData.membership_id),
          consultation_date: formData.consultation_date,
          status: formData.status,
          org_id: orgId,
        };
        const result = await createConsultation(createPayload).unwrap();
        
        if (result?.id) {
          setValue("id", result.id);
          setAction?.("edit");
          toast({
            description: "Consultation created successfully",
            variant: "success",
          });
        }
      } else {
        await updateConsultation({ ...formData, org_id: orgId }).unwrap();
        toast({
          description: "Consultation updated successfully",
          variant: "success",
        });
      }
      refetch?.();
    } catch (error: unknown) {
      console.error("Error", { error });
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save consultation details",
      });
    }
  };

  const handleSaveSection = async (section: string, questions: FormQuestionResponse[]) => {
    if (!consultationId) {
      toast({
        variant: "destructive",
        description: "Please save the consultation details first (click Next).",
      });
      return;
    }

    // Validate required fields in this section
    const questionNames = questions.map(q => `answers.${q.id}` as Path<Consultation>);
    const isSectionValid = await trigger(questionNames);

    if (!isSectionValid) {
      toast({
        variant: "destructive",
        description: "Please fill all mandatory fields in this section.",
      });
      return;
    }

    const answersPayload: ConsultationAnswer[] = [];
    questions.forEach((q) => {
      const value = watcher.answers?.[String(q.id)];
      if (value !== undefined && value !== null && value !== "") {
        answersPayload.push({
          question_id: q.id,
          question_type: q.question_type,
          answer: value,
        });
      }
    });

    if (answersPayload.length === 0) {
      toast({
        description: "No answers to save in this section.",
      });
      return;
    }

    setSavingSection(section);
    try {
      await submitConsultationAnswers({
        consultation_id: Number(consultationId),
        org_id: orgId,
        answers: answersPayload,
      }).unwrap();

      toast({
        variant: "success",
        description: `${section.charAt(0).toUpperCase() + section.slice(1)} answers saved successfully`,
      });
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save section answers",
      });
    } finally {
      setSavingSection(null);
    }
  };

  const getSectionProgress = (questions: FormQuestionResponse[]) => {
    if (!questions?.length) return { filled: 0, total: 0 };
    const total = questions.length;
    const filled = questions.filter((q) => {
      const answer = watcher.answers?.[String(q.id)];
      if (Array.isArray(answer)) return answer.length > 0;
      return answer !== undefined && answer !== null && answer !== "";
    }).length;
    return { filled, total };
  };

  const isSectionRequiredFilled = (questions: FormQuestionResponse[]) => {
    const requiredQuestions = questions.filter((q) => q.is_required && q.question_type !== "toggle");
    if (requiredQuestions.length === 0) return true;
    
    return requiredQuestions.every((q) => {
      const answer = watcher.answers?.[String(q.id)];
      if (Array.isArray(answer)) return answer.length > 0;
      return answer !== undefined && answer !== null && answer !== "";
    });
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between">
            <p className="font-medium text-sm">Consultation Details</p>
        </div>
        <Separator className="h-[1px] mt-2" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          {/* Member Selection */}
          <div>
            <Controller
              name="member_id"
              control={control}
              rules={{ required: "Member is required" }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Member</p>
                  <Combobox
                    key={`member-combobox-${value}-${memberResetKey}`}
                    list={memberOptions}
                    defaultValue={
                      memberOptions.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = memberOptions.find(
                        (option) => option.label === selectedLabel
                      );
                      if (selectedOption) {
                        onChange(Number(selectedOption.value));
                      } else {
                        onChange(undefined);
                      }
                    }}
                    label="Member"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Membership */}
          <div>
            <Controller
              name="membership_id"
              control={control}
              rules={{ required: "Membership is required" }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <p className="text-sm mb-2">Membership</p>
                  <Combobox
                    list={selectedMember?.membership_plans?.map((plan) => ({
                      value: plan.membership_plan_id?.toString(),
                      label: plan.membership_plan_name?.toString() || "",
                    }))}
                    defaultValue={
                      selectedMember?.membership_plans?.find(
                        (p) => p.membership_plan_id?.toString() === value?.toString()
                      )?.membership_plan_name?.toString() || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedPlan = selectedMember?.membership_plans?.find(
                        (p) => p.membership_plan_name?.toString() === selectedLabel
                      );
                      if (selectedPlan) {
                        onChange(Number(selectedPlan.membership_plan_id));
                      } else {
                        onChange(undefined);
                      }
                    }}
                    className="w-full"
                    disabled={!selectedMember}
                    label="Membership"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Coach Selection */}
          <div>
            <Controller
              name="coach_id"
              control={control}
              rules={{ required: "Coach is required" }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Coach</p>
                  <Combobox
                    list={coachOptions}
                    defaultValue={
                      coachOptions.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = coachOptions.find(
                        (option) => option.label === selectedLabel
                      );
                      if (selectedOption) {
                        onChange(Number(selectedOption.value));
                        // Also update coach_name
                        const selectedCoach = coachData?.data.find(
                          (c) => c.id?.toString() === selectedOption.value
                        );
                        if (selectedCoach) {
                          const firstName = (selectedCoach?.first_name || "").trim();
                          const lastName = (selectedCoach?.last_name || "").trim();
                          const fullName = [firstName, lastName]
                            .filter(Boolean)
                            .join(" ");
                          setValue("coach_name", fullName);
                        }
                      } else {
                        onChange(undefined);
                        setValue("coach_name", "");
                      }
                    }}
                    label="Coach"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Date Selection */}
          <div>
            <Controller
              name="consultation_date"
              control={control}
              rules={{ required: "Date is required" }}
              render={({
                field: { value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Date</p>
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <div className="relative flex-1">
                            <Button
                              variant={"outline"}
                              type="button"
                              disabled
                              className={cn(
                                "w-full pl-3 text-gray-800 text-left font-normal border-gray-300 border-[1px] cursor-not-allowed opacity-100"
                              )}
                            >
                              {value ? (
                                displayDate(new Date(value))
                              ) : (
                                <span>Select date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </div>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={value ? new Date(value) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              setValue("consultation_date", date.toISOString());
                            }
                          }}
                          disabled={(date) =>
                            date > new Date() || date < new Date("1900-01-01")
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Gender */}
          <div className="w-full">
             <p className="text-sm mb-2 opacity-0">Gender</p>
             <FloatingLabelInput
                label="Gender"
                disabled
                {...register("member_gender")}
             />
          </div>

          {/* Email */}
          <div className="w-full">
            <p className="text-sm mb-2 opacity-0">Email</p>
             <FloatingLabelInput
                label="Email"
                disabled
                {...register("member_email")}
             />
          </div>

          {/* Age */}
          <div className="w-full">
            <p className="text-sm mb-2 opacity-0">Age</p>
             <FloatingLabelInput
                label="Age"
                disabled
                value={calculatedAge || ""}
                onChange={() => {}}
             />
          </div>

          <div className="flex items-end">
            <LoadingButton 
                size="sm"
                type="button"
                onClick={handleSaveDetails}
                loading={isCreating || isUpdating}
                className="bg-primary text-white h-10 px-4 w-full"
            >
                {action === "add" ? "Create" : "Save Details"}
            </LoadingButton>
          </div>
        </div>
      </Card>

      <Accordion 
        type="multiple" 
        className="w-full space-y-4"
        value={openSections}
        onValueChange={setOpenSections}
      >
        {Object.entries(groupedQuestions).map(([section, questions], idx) => {
          const { filled, total } = getSectionProgress(questions);
          // Capitalize section name
          const sectionTitle = section.charAt(0).toUpperCase() + section.slice(1);

          return (
            <AccordionItem
              key={section}
              value={section}
              className="border border-gray-200 rounded-lg bg-white px-4"
            >
              <AccordionTrigger className="hover:no-underline py-4 hover:bg-transparent [&[data-state=open]]:bg-transparent">
                <div className="flex items-center justify-between w-full pr-4">
                  <span className="font-semibold text-gray-800">
                    {sectionTitle}
                  </span>
                  <Badge
                    variant="secondary"
                    className={cn(
                      "font-normal bg-primary text-white hover:bg-primary"
                    )}
                  >
                    {filled}/{total}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-6 px-1">
                <Separator className="mb-4" />
                {questions.map((q, qIdx) => (
                  <DynamicQuestionRenderer
                    key={q.id}
                    question={q}
                    control={control}
                    index={qIdx + 1}
                  />
                ))}

                <div className="mt-6 flex justify-end px-2">
                  <LoadingButton
                    type="button"
                    size="sm"
                    loading={savingSection === section}
                    onClick={() => handleSaveSection(section, questions)}
                    className="bg-primary text-white h-9 px-6"
                    disabled={!consultationId || (savingSection !== null && savingSection !== section) || !isSectionRequiredFilled(questions)}
                  >
                    Save {sectionTitle}
                  </LoadingButton>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default Background;

